import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { SearchIcon, WandIcon, BarChart3Icon } from 'lucide-react';
export function HowItWorksSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  return <section ref={ref} className="relative w-full py-24 md:py-32 px-4 bg-bg-secondary overflow-hidden" data-id="element-129">
      {/* Background Elements */}
      <div className="absolute top-0 left-0 w-full h-full" data-id="element-130">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-accent-green/5 rounded-full blur-3xl" data-id="element-131" />
        <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-accent-cyan/5 rounded-full blur-3xl" data-id="element-132" />
      </div>

      <div className="relative max-w-5xl mx-auto" data-id="element-133">
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.6
      }} className="text-center mb-16" data-id="element-134">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-4" data-id="element-135">
            How It <span className="text-accent-cyan" data-id="element-136">Works</span>
          </h2>
          <p className="text-text-muted text-lg max-w-2xl mx-auto" data-id="element-137">
            Three simple phases. One powerful system. Unlimited potential.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative" data-id="element-138">
          {/* Vertical Line */}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-accent-green via-accent-cyan to-accent-green" data-id="element-139" />

          {/* Step 1 */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative flex items-center mb-16" data-id="element-140">
            <div className="absolute left-8 md:left-1/2 w-4 h-4 -ml-2 rounded-full bg-accent-green glow-green" data-id="element-141" />
            <div className="ml-20 md:ml-0 md:w-1/2 md:pr-12" data-id="element-142">
              <div className="glass-card rounded-2xl p-6" data-id="element-143">
                <div className="flex items-center gap-4 mb-4" data-id="element-144">
                  <div className="w-12 h-12 rounded-xl bg-accent-green/10 flex items-center justify-center" data-id="element-145">
                    <SearchIcon className="w-6 h-6 text-accent-green" data-id="element-146" />
                  </div>
                  <div data-id="element-147">
                    <span className="text-accent-green text-sm font-semibold" data-id="element-148">
                      PHASE 1
                    </span>
                    <h3 className="text-xl font-bold text-white" data-id="element-149">Extract</h3>
                  </div>
                </div>
                <p className="text-text-muted" data-id="element-150">
                  Use the 3-Variable Profit Rule to identify niches with proven
                  demand, low competition, and high monetization potential. No
                  guessing—just data.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Step 2 */}
          <motion.div initial={{
          opacity: 0,
          x: 50
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.4
        }} className="relative flex items-center justify-end mb-16" data-id="element-151">
            <div className="absolute left-8 md:left-1/2 w-4 h-4 -ml-2 rounded-full bg-accent-cyan glow-cyan" data-id="element-152" />
            <div className="ml-20 md:ml-0 md:w-1/2 md:pl-12" data-id="element-153">
              <div className="glass-card rounded-2xl p-6" data-id="element-154">
                <div className="flex items-center gap-4 mb-4" data-id="element-155">
                  <div className="w-12 h-12 rounded-xl bg-accent-cyan/10 flex items-center justify-center" data-id="element-156">
                    <WandIcon className="w-6 h-6 text-accent-cyan" data-id="element-157" />
                  </div>
                  <div data-id="element-158">
                    <span className="text-accent-cyan text-sm font-semibold" data-id="element-159">
                      PHASE 2
                    </span>
                    <h3 className="text-xl font-bold text-white" data-id="element-160">Assemble</h3>
                  </div>
                </div>
                <p className="text-text-muted" data-id="element-161">
                  Deploy the AI Creative Stack to generate scripts, visuals, and
                  neural narration. Create a complete video in under 60 minutes.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Step 3 */}
          <motion.div initial={{
          opacity: 0,
          x: -50
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.6
        }} className="relative flex items-center" data-id="element-162">
            <div className="absolute left-8 md:left-1/2 w-4 h-4 -ml-2 rounded-full bg-accent-green glow-green" data-id="element-163" />
            <div className="ml-20 md:ml-0 md:w-1/2 md:pr-12" data-id="element-164">
              <div className="glass-card rounded-2xl p-6" data-id="element-165">
                <div className="flex items-center gap-4 mb-4" data-id="element-166">
                  <div className="w-12 h-12 rounded-xl bg-accent-green/10 flex items-center justify-center" data-id="element-167">
                    <BarChart3Icon className="w-6 h-6 text-accent-green" data-id="element-168" />
                  </div>
                  <div data-id="element-169">
                    <span className="text-accent-green text-sm font-semibold" data-id="element-170">
                      PHASE 3
                    </span>
                    <h3 className="text-xl font-bold text-white" data-id="element-171">Scale</h3>
                  </div>
                </div>
                <p className="text-text-muted" data-id="element-172">
                  Replicate the system across multiple channels. Build a
                  portfolio of anonymous assets generating compounding passive
                  income.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}